/**
 * Task Service
 * Handles task management, subtasks, and dependencies
 */

import { Task, TaskFormData, Status, Priority, Horizon } from '@/types'
import { BaseService } from './base.service'
import { StorageKeys } from './storage'
import {
  cleanUser,
  cleanUsers,
  cleanWorkspace,
  cleanProjects,
  cleanTask,
} from '@/lib/utils/clean-references'

class TaskService extends BaseService<Task> {
  constructor() {
    super(StorageKeys.TASKS)
  }

  /**
   * Create task with all relationships
   */
  async createTask(data: TaskFormData, creatorId: string): Promise<Task> {
    const { userService } = await import('./user.service')
    const { workspaceService } = await import('./workspace.service')
    const { projectService } = await import('./project.service')

    const creator = await userService.getById(creatorId)
    if (!creator) {
      throw new Error('Creator not found')
    }

    const workspace = await workspaceService.getById(data.workspaceId)
    if (!workspace) {
      throw new Error('Workspace not found')
    }

    const projects = data.projectIds ? await projectService.getByIds(data.projectIds) : []

    const assignee = data.assigneeId
      ? (await userService.getById(data.assigneeId)) || undefined
      : undefined

    const parentTask = data.parentTaskId
      ? (await this.getById(data.parentTaskId)) || undefined
      : undefined

    const dependencies = data.dependencyIds ? await this.getByIds(data.dependencyIds) : []

    // Build users array from userIds, creator, and assignee, avoiding duplicates
    const associatedUsers = data.userIds ? await userService.getByIds(data.userIds) : []
    const usersSet = new Set<string>()
    const users: import('@/types').User[] = []

    // Add creator
    usersSet.add(creator.id)
    users.push(creator)

    // Add assignee if different from creator
    if (assignee && !usersSet.has(assignee.id)) {
      usersSet.add(assignee.id)
      users.push(assignee)
    }

    // Add associated users
    for (const user of associatedUsers) {
      if (!usersSet.has(user.id)) {
        usersSet.add(user.id)
        users.push(user)
      }
    }

    // Clean all references to prevent circular reference issues
    const task = await this.create({
      creator: cleanUser(creator),
      type: data.type,
      name: data.name,
      description: data.description,
      status: data.status,
      priority: data.priority,
      horizon: data.horizon,
      dueDate: data.dueDate,
      assignee: assignee ? cleanUser(assignee) : undefined,
      parentTask: parentTask ? cleanTask(parentTask) : undefined,
      workspace: cleanWorkspace(workspace),
      projects: cleanProjects(projects),
      subtasks: [],
      messages: [],
      documents: [],
      dependencies: dependencies.map(cleanTask),
      users: cleanUsers(users),
    })

    // Update projects to include this task in their tasks array
    for (const project of projects) {
      const cleanedTask = cleanTask(task)
      await projectService.update(project.id, {
        tasks: [...project.tasks, cleanedTask],
      } as Partial<import('@/types').Project>)
    }

    return task
  }

  /**
   * Get tasks for workspace
   */
  async getTasksForWorkspace(workspaceId: string): Promise<Task[]> {
    return this.filter((task) => task.workspace.id === workspaceId)
  }

  /**
   * Get tasks for project
   */
  async getTasksForProject(projectId: string): Promise<Task[]> {
    return this.filter((task) => task.projects.some((p) => p.id === projectId))
  }

  /**
   * Get tasks for user (assigned)
   */
  async getTasksForUser(userId: string): Promise<Task[]> {
    return this.filter((task) => task.assignee?.id === userId)
  }

  /**
   * Get tasks by status
   */
  async getTasksByStatus(status: Status): Promise<Task[]> {
    return this.filter((task) => task.status === status)
  }

  /**
   * Get tasks by priority
   */
  async getTasksByPriority(priority: Priority): Promise<Task[]> {
    return this.filter((task) => task.priority === priority)
  }

  /**
   * Get tasks by horizon
   */
  async getTasksByHorizon(horizon: Horizon): Promise<Task[]> {
    return this.filter((task) => task.horizon === horizon)
  }

  /**
   * Get subtasks for parent task
   */
  async getSubtasks(parentTaskId: string): Promise<Task[]> {
    return this.filter((task) => task.parentTask?.id === parentTaskId)
  }

  /**
   * Get overdue tasks
   */
  async getOverdueTasks(): Promise<Task[]> {
    const now = new Date()
    return this.filter(
      (task) => !!task.dueDate && new Date(task.dueDate) < now && task.status !== Status.Done
    )
  }

  /**
   * Assign task to user
   */
  async assignTask(taskId: string, userId: string): Promise<Task> {
    const { userService } = await import('./user.service')
    const user = await userService.getById(userId)

    if (!user) {
      throw new Error('User not found')
    }

    // Clean user to prevent circular reference issues
    return this.update(taskId, { assignee: cleanUser(user) } as Partial<Task>)
  }

  /**
   * Unassign task
   */
  async unassignTask(taskId: string): Promise<Task> {
    return this.update(taskId, { assignee: undefined } as Partial<Task>)
  }

  /**
   * Update task status
   */
  async updateStatus(taskId: string, status: Status): Promise<Task> {
    return this.update(taskId, { status } as Partial<Task>)
  }

  /**
   * Update task priority
   */
  async updatePriority(taskId: string, priority: Priority): Promise<Task> {
    return this.update(taskId, { priority } as Partial<Task>)
  }

  /**
   * Add dependency
   */
  async addDependency(taskId: string, dependencyId: string): Promise<Task> {
    const task = await this.getById(taskId)
    if (!task) {
      throw new Error('Task not found')
    }

    const dependency = await this.getById(dependencyId)
    if (!dependency) {
      throw new Error('Dependency task not found')
    }

    // Check for circular dependencies
    if (await this.wouldCreateCircularDependency(taskId, dependencyId)) {
      throw new Error('Cannot add dependency: would create circular dependency')
    }

    if (task.dependencies.some((d) => d.id === dependencyId)) {
      throw new Error('Dependency already exists')
    }

    // Clean dependency to prevent circular reference issues
    return this.update(taskId, {
      dependencies: [...task.dependencies, cleanTask(dependency)],
    } as Partial<Task>)
  }

  /**
   * Remove dependency
   */
  async removeDependency(taskId: string, dependencyId: string): Promise<Task> {
    const task = await this.getById(taskId)
    if (!task) {
      throw new Error('Task not found')
    }

    return this.update(taskId, {
      dependencies: task.dependencies.filter((d) => d.id !== dependencyId),
    } as Partial<Task>)
  }

  /**
   * Check if adding dependency would create circular dependency
   */
  private async wouldCreateCircularDependency(
    taskId: string,
    dependencyId: string
  ): Promise<boolean> {
    const visited = new Set<string>()

    async function checkCircular(currentId: string): Promise<boolean> {
      if (currentId === taskId) return true
      if (visited.has(currentId)) return false

      visited.add(currentId)

      const task = await taskService.getById(currentId)
      if (!task) return false

      for (const dep of task.dependencies) {
        if (await checkCircular(dep.id)) {
          return true
        }
      }

      return false
    }

    return checkCircular(dependencyId)
  }

  /**
   * Get task by ID with populated messages
   * This ensures messages are always loaded from message storage
   */
  async getByIdWithMessages(taskId: string): Promise<Task | null> {
    const task = await this.getById(taskId)
    if (!task) return null

    // Get messages from message storage
    const { messageService } = await import('./message.service')
    const allMessages = await messageService.getAll()
    const taskMessages = allMessages.filter((message) => message.tasks.some((t) => t.id === taskId))

    return {
      ...task,
      messages: taskMessages || [],
    }
  }

  /**
   * Search tasks
   */
  async searchTasks(query: string): Promise<Task[]> {
    return this.search(query, ['name', 'description'])
  }

  /**
   * Get task dependencies status
   */
  async getDependenciesStatus(taskId: string): Promise<{
    total: number
    completed: number
    allCompleted: boolean
  }> {
    const task = await this.getById(taskId)
    if (!task) {
      throw new Error('Task not found')
    }

    const total = task.dependencies.length
    const completed = task.dependencies.filter((d) => d.status === Status.Done).length

    return {
      total,
      completed,
      allCompleted: total === completed,
    }
  }

  /**
   * Get tasks that depend on this task (blocked by this task)
   */
  async getBlockedTasks(taskId: string): Promise<Task[]> {
    return this.filter((task) => task.dependencies.some((d) => d.id === taskId))
  }

  /**
   * Override update to sync with projects and dependencies
   */
  async update(id: string, data: Partial<Task>): Promise<Task> {
    const { projectService } = await import('./project.service')
    const oldTask = await this.getById(id)

    if (!oldTask) {
      throw new Error('Task not found')
    }

    // Perform the update
    const updatedTask = await super.update(id, data)

    // If projects changed, sync with projects
    if (data.projects) {
      const oldProjectIds = oldTask.projects.map((p) => p.id)
      const newProjectIds = data.projects.map((p) => p.id)

      // Remove task from old projects
      for (const projectId of oldProjectIds) {
        if (!newProjectIds.includes(projectId)) {
          const project = await projectService.getById(projectId)
          if (project) {
            await projectService.update(projectId, {
              tasks: project.tasks.filter((t) => t.id !== id),
            } as Partial<import('@/types').Project>)
          }
        }
      }

      // Add task to new projects
      for (const projectId of newProjectIds) {
        if (!oldProjectIds.includes(projectId)) {
          const project = await projectService.getById(projectId)
          if (project) {
            const cleanedTask = cleanTask(updatedTask)
            await projectService.update(projectId, {
              tasks: [...project.tasks, cleanedTask],
            } as Partial<import('@/types').Project>)
          }
        }
      }

      // Update task in existing projects (in case task details changed)
      for (const projectId of newProjectIds) {
        if (oldProjectIds.includes(projectId)) {
          const project = await projectService.getById(projectId)
          if (project) {
            const cleanedTask = cleanTask(updatedTask)
            await projectService.update(projectId, {
              tasks: project.tasks.map((t) => (t.id === id ? cleanedTask : t)),
            } as Partial<import('@/types').Project>)
          }
        }
      }
    }

    // Update this task in all other tasks' dependencies arrays
    if (data.status || data.name || data.description) {
      const allTasks = await super.getAll()
      const tasksToUpdate = allTasks.filter((task) =>
        task.dependencies.some((dep) => dep.id === id)
      )

      for (const task of tasksToUpdate) {
        const cleanedTask = cleanTask(updatedTask)
        const updatedDependencies = task.dependencies.map((dep) =>
          dep.id === id ? cleanedTask : dep
        )

        await super.update(task.id, {
          dependencies: updatedDependencies,
        } as Partial<Task>)
      }
    }

    return updatedTask
  }

  /**
   * Override delete to sync with projects and dependencies
   */
  async delete(id: string): Promise<void> {
    const { projectService } = await import('./project.service')
    const task = await this.getById(id)

    if (task) {
      // Remove task from all associated projects
      for (const project of task.projects) {
        const fullProject = await projectService.getById(project.id)
        if (fullProject) {
          await projectService.update(project.id, {
            tasks: fullProject.tasks.filter((t) => t.id !== id),
          } as Partial<import('@/types').Project>)
        }
      }

      // Remove this task from all other tasks' dependencies arrays
      const allTasks = await super.getAll()
      const tasksToUpdate = allTasks.filter((t) => t.dependencies.some((dep) => dep.id === id))

      for (const taskToUpdate of tasksToUpdate) {
        await super.update(taskToUpdate.id, {
          dependencies: taskToUpdate.dependencies.filter((dep) => dep.id !== id),
        } as Partial<Task>)
      }
    }

    // Perform the delete
    await super.delete(id)
  }
}

export const taskService = new TaskService()
